import { ReactElement, useMemo } from 'react'
import { TradeType, CurrencyAmount, Currency, Percent } from '@pancakeswap/sdk'
import { Button, Text, ErrorIcon } from '@pancakeswap/uikit'
import { Field } from '@/state/swap/actions'
import { useTranslation } from '@pancakeswap/localization'
import { formatAmount } from '@pancakeswap/utils/formatFractions'
import { warningSeverity, basisPointsToPercent } from '@/utils/exchange'
import { RowBetween, RowFixed } from '@/components/Layout/Row'
import truncateHash from '@pancakeswap/utils/truncateHash'
import { Typography } from '@/components/ui/typography'
import { ArrowDownIcon } from '@radix-ui/react-icons'
import { SwapShowAcceptChanges } from './styleds'
import { CurrencyLogo } from '@/components/currency-logo'

export default function SwapModalHeader({
  inputAmount,
  outputAmount,
  tradeType,
  currencyBalances,
  priceImpactWithoutFee,
  slippageAdjustedAmounts,
  isEnoughInputBalance,
  recipient,
  showAcceptChanges,
  onAcceptChanges,
  allowedSlippage,
}: {
  inputAmount: CurrencyAmount<Currency>
  outputAmount: CurrencyAmount<Currency>
  currencyBalances: {
    INPUT?: CurrencyAmount<Currency>
    OUTPUT?: CurrencyAmount<Currency>
  }
  tradeType: TradeType
  priceImpactWithoutFee?: Percent
  slippageAdjustedAmounts: { [field in Field]?: CurrencyAmount<Currency> }
  isEnoughInputBalance?: boolean
  recipient?: string
  showAcceptChanges: boolean
  onAcceptChanges: () => void
  allowedSlippage: number | ReactElement
}) {
  const { t } = useTranslation()

  const priceImpactSeverity = warningSeverity(priceImpactWithoutFee)

  const inputTextColor =
    showAcceptChanges && tradeType === TradeType.EXACT_OUTPUT && isEnoughInputBalance
      ? 'primary'
      : tradeType === TradeType.EXACT_OUTPUT && !isEnoughInputBalance
      ? 'failure'
      : 'text'

  const amount =
    tradeType === TradeType.EXACT_INPUT
      ? formatAmount(slippageAdjustedAmounts[Field.OUTPUT], 6)
      : formatAmount(slippageAdjustedAmounts[Field.INPUT], 6)
  const symbol = tradeType === TradeType.EXACT_INPUT ? outputAmount.currency.symbol : inputAmount.currency.symbol

  const tradeInfoText = useMemo(() => {
    return tradeType === TradeType.EXACT_INPUT
      ? t('Output is estimated. You will receive at least %amount% %symbol% or the transaction will revert.', {
          amount: `${amount}`,
          symbol,
        })
      : t('Input is estimated. You will sell at most %amount% %symbol% or the transaction will revert.', {
          amount: `${amount}`,
          symbol,
        })
  }, [t, tradeType, amount, symbol])

  const truncatedRecipient = recipient ? truncateHash(recipient) : ''

  const recipientInfoText = t('Output will be sent to %recipient%', {
    recipient: truncatedRecipient,
  })

  const [recipientSentToText, postSentToText] = recipientInfoText.split(truncatedRecipient)

  return (
    <div>
      <div className={'flex justify-between'}>
        <div className={'flex items-center gap-1'}>
          <CurrencyLogo chainId={inputAmount.currency.chainId} address={inputAmount.currency.wrapped.address} />
          <Typography>{inputAmount.currency.symbol}</Typography>
        </div>
        <Typography variant={'h4'}>{formatAmount(inputAmount, 6)}</Typography>
      </div>
      <div className={'flex justify-end my-6'}>
        <ArrowDownIcon width={20} height={20} />
      </div>
      <div className={'flex justify-between'}>
        <div className={'flex items-center gap-1'}>
          <CurrencyLogo chainId={outputAmount.currency.chainId} address={outputAmount.currency.wrapped.address} />
          <Typography>{outputAmount.currency.symbol}</Typography>
        </div>
        <div>
          <Typography
            variant={'h4'}
            color={
              priceImpactSeverity > 2
                ? 'destructive'
                : showAcceptChanges && tradeType === TradeType.EXACT_INPUT
                ? undefined
                : 'primary'
            }
          >
            {formatAmount(outputAmount, 6)}
          </Typography>
        </div>
      </div>
      {showAcceptChanges ? (
        <SwapShowAcceptChanges justify="flex-start" gap="0px">
          <RowBetween>
            <RowFixed>
              <ErrorIcon mr="8px" />
              <Text bold> {t('Price Updated')}</Text>
            </RowFixed>
            <Button onClick={onAcceptChanges}>{t('Accept')}</Button>
          </RowBetween>
        </SwapShowAcceptChanges>
      ) : null}
      <div className={'mt-6'}>
        <div className={'flex justify-between items-center mb-2'}>
          <Typography variant={'small'}>{t('Slippage Tolerance')}</Typography>
          <Typography className={'text-primary'}>
            {typeof allowedSlippage === 'number'
              ? `${basisPointsToPercent(allowedSlippage).toFixed(2)}%`
              : allowedSlippage}
          </Typography>
        </div>
        {tradeType === TradeType.EXACT_OUTPUT && !isEnoughInputBalance && (
          <Typography variant={'tip'}>{t('Insufficient input token balance. Your transaction may fail.')}</Typography>
        )}
        <Typography variant={'tip'}>{tradeInfoText}</Typography>
      </div>
      {recipient ? (
        <div className={'mt-4'}>
          <Typography variant={'small'}>
            {recipientSentToText}
            <b title={recipient}>{truncatedRecipient}</b>
            {postSentToText}
          </Typography>
        </div>
      ) : null}
    </div>
  )
}
